import type { ShapeTessellation, Vec2, WallGrid, WallMetrics } from '../types';
import { tessellateConvexPolygon } from './tessellate';

/**
 * The shape registry.
 *
 * A shape owns three things: the footprint of a single panel, how panels are
 * laid out into a wall, and how the footprint is tessellated. Everything
 * downstream — the height field, the mesh builder, the exporters, the
 * viewport — works off those.
 *
 * Two conventions hold across every shape:
 *
 * - **`size` is the panel's overall width**, so the 50–250 mm control means the
 *   same physical thing whichever shape is selected.
 * - **The outline is counter-clockwise and centred on the panel's own origin.**
 *   The mesh builder stitches the skirt from the boundary loop in that order,
 *   so a reversed winding turns the panel inside out.
 */
export interface ShapeDefinition {
  id: string;
  name: string;
  description: string;
  /** Roadmap phase the shape belongs to. */
  phase: 1 | 2 | 3;

  /**
   * Panel outline in panel space, counter-clockwise, centred on the origin.
   *
   * `gap` is passed because a shape may have to realise the mounting gap by
   * shrinking its footprint rather than by spreading the lattice. Squares,
   * hexagons and rhombi space their lattice and ignore it; triangles cannot,
   * because separating an up/down pair moves them along a different axis than
   * separating two rows, so they inset instead.
   */
  outline(size: number, gap: number): Vec2[];

  /**
   * Optional specialised tessellation. Shapes that do not provide one fall back
   * to a centroid fan of `outline`, which is correct for any convex footprint.
   */
  tessellate?(size: number, gap: number, segments: number): ShapeTessellation;

  /** Centre of the panel at `(column, row)` in wall space. */
  layout(column: number, row: number, size: number, gap: number): Vec2;

  /**
   * Rotation of the panel at `(column, row)`, in degrees. Only shapes that
   * alternate orientation to tile — triangles — need this.
   */
  orientation?(column: number, row: number): number;

  /** Wall-level measurements for a whole grid. */
  metrics(grid: WallGrid, size: number, gap: number): WallMetrics;

  /** Label for the grid controls, when "columns and rows" would mislead. */
  gridHint?: string;
}

const shapes = new Map<string, ShapeDefinition>();

export function registerShape(definition: ShapeDefinition): ShapeDefinition {
  if (shapes.has(definition.id)) {
    throw new Error(`Shape "${definition.id}" is already registered.`);
  }
  shapes.set(definition.id, definition);
  return definition;
}

export function getShape(id: string): ShapeDefinition | undefined {
  return shapes.get(id);
}

export function requireShape(id: string): ShapeDefinition {
  const definition = shapes.get(id);
  if (!definition) {
    throw new Error(`Unknown shape "${id}". Registered: ${[...shapes.keys()].join(', ')}`);
  }
  return definition;
}

export function listShapes(): ShapeDefinition[] {
  return [...shapes.values()];
}

/** Shapes available in the current release phase. */
export function listShapesUpToPhase(phase: number): ShapeDefinition[] {
  return listShapes().filter((shape) => shape.phase <= phase);
}

/**
 * Resolves a shape's tessellation, applying the convex-fan fallback.
 *
 * The fan subdivides each sector along its own radius, so passing `segments`
 * straight through would mean different sample spacings for different shapes:
 * a hexagon's radius is half its width, so it would come out twice as fine as
 * a square and roughly three times as heavy at the same setting. Scaling by the
 * radius makes `segments` mean the same physical spacing whatever the
 * footprint, which is what the resolution control claims it means.
 */
export function tessellateShape(
  shape: ShapeDefinition,
  size: number,
  gap: number,
  segments: number,
): ShapeTessellation {
  if (shape.tessellate) return shape.tessellate(size, gap, segments);

  const outline = shape.outline(size, gap);
  let radius = 0;
  for (const point of outline) {
    radius = Math.max(radius, Math.hypot(point.x, point.y));
  }

  const scaled = Math.max(1, Math.round((segments * radius) / Math.max(size, 1e-6)));
  return tessellateConvexPolygon(outline, scaled);
}

/**
 * Wall bounds derived from where the panels actually land.
 *
 * Computing this from the real placements rather than from a closed-form
 * guess is what keeps `metrics` honest on staggered layouts, where the offset
 * rows and the alternating orientations make the arithmetic easy to get subtly
 * wrong.
 */
export function wallBoundsFor(
  shape: ShapeDefinition,
  grid: WallGrid,
  size: number,
  gap: number,
): { width: number; height: number } {
  const outline = shape.outline(size, gap);
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (let row = 0; row < grid.rows; row += 1) {
    for (let column = 0; column < grid.columns; column += 1) {
      const centre = shape.layout(column, row, size, gap);
      const angle = ((shape.orientation?.(column, row) ?? 0) * Math.PI) / 180;
      const cos = Math.cos(angle);
      const sin = Math.sin(angle);

      for (const point of outline) {
        const x = centre.x + point.x * cos - point.y * sin;
        const y = centre.y + point.x * sin + point.y * cos;
        if (x < minX) minX = x;
        if (y < minY) minY = y;
        if (x > maxX) maxX = x;
        if (y > maxY) maxY = y;
      }
    }
  }

  return { width: maxX - minX, height: maxY - minY };
}

/** Test-only escape hatch so suites can start from an empty registry. */
export function clearShapes(): void {
  shapes.clear();
}
